import * as React from 'react';

import Button from 'components/atoms/v2/Button';
import LoadingIndicator from 'components/atoms/LoadingIndicator';
import { fetchProductOptions } from 'libs/apis/services/ProductOptionsService';
import { addCartProduct } from 'libs/apis/services/CartsService';
import { fmtLimitedNum, fmtPriceWithYen } from 'libs/formatter';
import type { GetProductOptionsResponse } from 'libs/apis/generated/models/GetProductOptionsResponse';
import type { ProductOption as ProductOptionResponse } from 'libs/apis/generated/models/ProductOption';
import type { OptionUnit as OptionUnitResponse } from 'libs/apis/generated/models/OptionUnit';
import useBodyScrollLock from 'libs/useBodyScrollLock';
import classes from './GiftBoxSkuSelectModal.scss';

type StockStatus = 'in-stock' | 'sold-out' | 'discontinued';

type GiftBoxUnit = {
  id: number;
  name: string;
  price: number;
  sizePoint: number;
  stock: number;
  stockStatus: StockStatus;
  imageUrl?: string;
};

type GiftBoxOption = {
  id: number;
  name: string;
  units: GiftBoxUnit[];
};

type GiftBoxModalData = {
  giftBoxMaxPoint: number;
  defaultUnitTotalPrice: number;
  options: GiftBoxOption[];
};

type SelectionEntry = {
  selectedUnitId: number | null;
  quantitiesByUnitId: Record<number, number>;
};

type SelectionState = Record<number, SelectionEntry>;

type Props = {
  productId: number;
  isPreview: boolean;
  preferEGift?: boolean;
  triggerElement: HTMLButtonElement;
};

type SummaryRow = {
  optionId: number;
  optionName: string;
  unitId: number;
  unitName: string;
  price: number;
  sizePoint: number;
  quantity: number;
};

type ReconcileResult = {
  selection: SelectionState;
  clearedMessages: string[];
};

const FETCH_ERROR_MESSAGE = '商品情報の取得に失敗しました。時間をおいて再度お試しください。';
const STOCK_DISPLAY_LIMIT = 10;
const resolveStockStatus = (unit: OptionUnitResponse): StockStatus => {
  if (unit.stock > 0) {
    return 'in-stock';
  }
  if (unit.isDiscontinued) {
    return 'discontinued';
  }
  return 'sold-out';
};

const toGiftBoxUnit = (unit: OptionUnitResponse): GiftBoxUnit => ({
  id: unit.id,
  name: unit.name,
  price: unit.price,
  sizePoint: unit.sizePoint ?? 0,
  stock: unit.stock,
  stockStatus: resolveStockStatus(unit),
  imageUrl: unit.imageUrl,
});

const toGiftBoxOption = (option: ProductOptionResponse): GiftBoxOption => ({
  id: option.id,
  name: option.name,
  units: option.units.map(toGiftBoxUnit).filter((unit) => unit.sizePoint > 0),
});

const toGiftBoxModalData = (response: GetProductOptionsResponse): GiftBoxModalData => ({
  giftBoxMaxPoint: response.giftBoxMaxPoint,
  defaultUnitTotalPrice: response.defaultUnitTotalPrice,
  options: response.options.map(toGiftBoxOption).filter((option) => option.units.length > 0),
});

const buildInitialSelection = (options: GiftBoxOption[]): SelectionState =>
  options.reduce<SelectionState>((acc, option) => {
    const defaultUnit = option.units[0];
    acc[option.id] = {
      selectedUnitId: defaultUnit?.id ?? null,
      quantitiesByUnitId: option.units.reduce<Record<number, number>>((unitAcc, unit) => {
        unitAcc[unit.id] = 0;
        return unitAcc;
      }, {}),
    };
    return acc;
  }, {});

const reconcileSelection = (options: GiftBoxOption[], prevSelection: SelectionState): ReconcileResult =>
  options.reduce<ReconcileResult>(
    (acc, option) => {
      const fallbackUnit = option.units[0];
      const prevEntry = prevSelection[option.id];
      const hasSelectedUnit = option.units.some((unit) => unit.id === prevEntry?.selectedUnitId);
      const nextQuantitiesByUnitId = option.units.reduce<Record<number, number>>((unitAcc, unit) => {
        const prevQuantity = prevEntry?.quantitiesByUnitId?.[unit.id] ?? 0;
        const shouldClearQuantity = prevQuantity > 0 && (unit.stock <= 0 || prevQuantity > unit.stock);

        unitAcc[unit.id] = shouldClearQuantity ? 0 : prevQuantity;
        if (shouldClearQuantity) {
          acc.clearedMessages.push(unit.name);
        }

        return unitAcc;
      }, {});
      const nextSelectedUnit =
        option.units.find((unit) => unit.id === prevEntry?.selectedUnitId) ?? fallbackUnit ?? null;
      const selectedQuantity = nextSelectedUnit ? nextQuantitiesByUnitId[nextSelectedUnit.id] ?? 0 : 0;
      const hasRemainingQuantities = Object.values(nextQuantitiesByUnitId).some((quantity) => quantity > 0);
      const shouldClearSelection =
        prevEntry?.selectedUnitId !== null && selectedQuantity <= 0 && !hasRemainingQuantities;

      acc.selection[option.id] = {
        selectedUnitId: shouldClearSelection
          ? null
          : hasSelectedUnit
          ? prevEntry?.selectedUnitId ?? fallbackUnit?.id ?? null
          : fallbackUnit?.id ?? null,
        quantitiesByUnitId: nextQuantitiesByUnitId,
      };

      return acc;
    },
    { selection: {}, clearedMessages: [] }
  );

const formatUnitMeta = (unit: GiftBoxUnit): string =>
  `${fmtPriceWithYen(unit.price)} / ${unit.sizePoint}pt / 在庫 ${fmtLimitedNum(unit.stock, STOCK_DISPLAY_LIMIT)}`;

const getSelectedUnit = (option: GiftBoxOption, selection: SelectionState): GiftBoxUnit | null => {
  const selectedUnitId = selection[option.id]?.selectedUnitId;
  return option.units.find((unit) => unit.id === selectedUnitId) ?? option.units[0] ?? null;
};

const getQuantity = (optionId: number, selection: SelectionState): number => {
  const entry = selection[optionId];
  if (!entry || entry.selectedUnitId === null) {
    return 0;
  }

  return entry.quantitiesByUnitId[entry.selectedUnitId] ?? 0;
};

const buildSummaryRows = (options: GiftBoxOption[], selection: SelectionState): SummaryRow[] =>
  options.flatMap((option) => {
    const entry = selection[option.id];
    if (!entry) {
      return [];
    }

    return option.units.flatMap((unit) => {
      const quantity = entry.quantitiesByUnitId[unit.id] ?? 0;
      if (quantity <= 0) {
        return [];
      }

      return [
        {
          optionId: option.id,
          optionName: option.name,
          unitId: unit.id,
          unitName: unit.name,
          price: unit.price,
          sizePoint: unit.sizePoint,
          quantity,
        },
      ];
    });
  });

const getUsedCapacity = (rows: SummaryRow[]): number =>
  rows.reduce((sum, row) => sum + row.sizePoint * row.quantity, 0);

const getSelectedItemsSubtotal = (rows: SummaryRow[]): number =>
  rows.reduce((sum, row) => sum + row.price * row.quantity, 0);

const buildSelectionAlertMessage = (messages: string[]): string => {
  if (messages.length === 0) {
    return '';
  }

  const itemLines = messages.map((message) => `・${message}`);
  return ['以下のSKUは在庫数が変動したため、選択を解除いたしました。', ...itemLines].join('\n');
};

const repeatUnitIds = (unitId: number, quantity: number): number[] => Array.from({ length: quantity }, () => unitId);

const GiftBoxSkuSelectModal: React.FC<Props> = React.memo((props) => {
  const { productId, isPreview, preferEGift = false, triggerElement } = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const [modalData, setModalData] = React.useState<GiftBoxModalData | null>(null);
  const [selection, setSelection] = React.useState<SelectionState>({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [isAdding, setIsAdding] = React.useState(false);
  const [fetchError, setFetchError] = React.useState<string | null>(null);
  const [cartMessage, setCartMessage] = React.useState<string | null>(null);
  const [selectionMessages, setSelectionMessages] = React.useState<string[]>([]);

  useBodyScrollLock(isOpen);

  const loadModalData = React.useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setFetchError(null);

    const result = await fetchProductOptions({ productId });
    if (result.isSuccess()) {
      const nextModalData = toGiftBoxModalData(result.value);
      setModalData(nextModalData);
      setSelection((prev) => {
        if (Object.keys(prev).length === 0) {
          setSelectionMessages([]);
          return buildInitialSelection(nextModalData.options);
        }

        const reconciled = reconcileSelection(nextModalData.options, prev);
        setSelectionMessages(reconciled.clearedMessages);
        return reconciled.selection;
      });
      setIsLoading(false);
      return true;
    }

    setFetchError(result.error.message ?? FETCH_ERROR_MESSAGE);
    setIsLoading(false);
    return false;
  }, [productId]);

  const openModal = React.useCallback(async () => {
    if (isLoading) {
      return;
    }

    const isLoaded = await loadModalData();
    setIsOpen(true);
    if (!isLoaded) {
      setModalData(null);
    }
  }, [isLoading, loadModalData]);

  const closeModal = React.useCallback(() => {
    setIsOpen(false);
    setCartMessage(null);
    setSelectionMessages([]);
  }, []);

  React.useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      event.preventDefault();
      void openModal();
    };

    triggerElement.addEventListener('click', clickHandler);
    return () => {
      triggerElement.removeEventListener('click', clickHandler);
    };
  }, [openModal, triggerElement]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('keydown', keyHandler);
    };
  }, [closeModal, isOpen]);

  React.useEffect(() => {
    if (!isOpen || selectionMessages.length === 0) {
      return;
    }

    window.alert(buildSelectionAlertMessage(selectionMessages));
    setSelectionMessages([]);
  }, [isOpen, selectionMessages]);

  const summaryRows = React.useMemo(
    () => (modalData ? buildSummaryRows(modalData.options, selection) : []),
    [modalData, selection]
  );
  const usedCapacity = getUsedCapacity(summaryRows);
  const subtotal = getSelectedItemsSubtotal(summaryRows);
  const totalPrice = (modalData?.defaultUnitTotalPrice ?? 0) + subtotal;
  const capacityRatio = modalData?.giftBoxMaxPoint
    ? Math.min((usedCapacity / modalData.giftBoxMaxPoint) * 100, 100)
    : 0;

  const groupedSummaryRows = summaryRows.reduce<Record<number, SummaryRow[]>>((acc, row) => {
    if (!acc[row.optionId]) {
      acc[row.optionId] = [];
    }
    acc[row.optionId].push(row);
    return acc;
  }, {});
  const hasSelectedItems = summaryRows.length > 0;

  const updateSelectedUnit = React.useCallback((optionId: number, unitId: number) => {
    setCartMessage(null);
    setSelectionMessages([]);
    setSelection((prev) => ({
      ...prev,
      [optionId]: {
        ...prev[optionId],
        selectedUnitId: unitId,
        quantitiesByUnitId: {
          ...prev[optionId]?.quantitiesByUnitId,
          [unitId]: prev[optionId]?.quantitiesByUnitId?.[unitId] ?? 0,
        },
      },
    }));
  }, []);

  const updateQuantity = React.useCallback(
    (optionId: number, delta: number) => {
      if (!modalData) {
        return;
      }

      setCartMessage(null);
      setSelectionMessages([]);
      setSelection((prev) => {
        const option = modalData.options.find((item) => item.id === optionId);
        if (!option) {
          return prev;
        }

        const selectedUnit = getSelectedUnit(option, prev);
        if (!selectedUnit) {
          return prev;
        }

        const entry = prev[optionId];
        const currentQuantity = entry?.quantitiesByUnitId?.[selectedUnit.id] ?? 0;
        const nextQuantity = currentQuantity + delta;
        if (nextQuantity < 0 || nextQuantity > selectedUnit.stock) {
          return prev;
        }

        const currentUsedCapacity = getUsedCapacity(buildSummaryRows(modalData.options, prev));
        if (
          delta > 0 &&
          modalData.giftBoxMaxPoint > 0 &&
          currentUsedCapacity + selectedUnit.sizePoint > modalData.giftBoxMaxPoint
        ) {
          return prev;
        }

        return {
          ...prev,
          [optionId]: {
            ...entry,
            selectedUnitId: selectedUnit.id,
            quantitiesByUnitId: {
              ...entry?.quantitiesByUnitId,
              [selectedUnit.id]: nextQuantity,
            },
          },
        };
      });
    },
    [modalData]
  );

  const resetSelection = React.useCallback(() => {
    if (!modalData) {
      return;
    }
    setCartMessage(null);
    setSelectionMessages([]);
    setSelection(buildInitialSelection(modalData.options));
  }, [modalData]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      closeModal();
    }
  };

  const handleCartButtonClick = React.useCallback(async () => {
    if (!hasSelectedItems || isAdding || isPreview) {
      return;
    }

    setIsAdding(true);
    setCartMessage(null);
    const result = await addCartProduct({
      productId,
      preferEGift,
      options: summaryRows.map((row) => ({
        optionId: row.optionId,
        unitIds: repeatUnitIds(row.unitId, row.quantity),
      })),
    });

    if (result.isSuccess()) {
      window.location.href = '/carts';
      return;
    }

    setCartMessage(result.error.message);
    setIsAdding(false);
  }, [hasSelectedItems, isAdding, isPreview, preferEGift, productId, summaryRows]);

  const renderBody = () => {
    if (isLoading || fetchError || !modalData) {
      return (
        <div className={classes['giftBoxSkuSelectModal__placeholder']}>
          {isLoading ? <LoadingIndicator size="lg" /> : fetchError ?? FETCH_ERROR_MESSAGE}
        </div>
      );
    }

    return (
      <>
        <p className={classes['giftBoxSkuSelectModal__lead']}>
          お好きなセレクトアイテムを組み合わせて、オリジナルのギフトボックスをお作りいただけます。
        </p>

        <section
          className={`${classes['giftBoxSkuSelectModal__section']} ${classes['giftBoxSkuSelectModal__section--capacity']}`}
        >
          <div className={classes['giftBoxSkuSelectModal__capacityHeader']}>
            <h3 className={classes['giftBoxSkuSelectModal__sectionTitle']}>容量ゲージ</h3>
            <div className={classes['giftBoxSkuSelectModal__capacityStatus']}>
              <span className={classes['giftBoxSkuSelectModal__capacityCaption']}>使用量 / 容量上限</span>
              <strong className={classes['giftBoxSkuSelectModal__capacityValue']}>
                {usedCapacity} / {modalData.giftBoxMaxPoint} pt
              </strong>
            </div>
          </div>
          <div className={classes['giftBoxSkuSelectModal__capacityBar']}>
            <div
              className={classes['giftBoxSkuSelectModal__capacityFill']}
              style={{ width: `${capacityRatio}%` }}
            />
          </div>
        </section>

        <section className={classes['giftBoxSkuSelectModal__section']}>
          <h3 className={classes['giftBoxSkuSelectModal__sectionTitle']}>セレクトアイテム一覧</h3>
          <div className={classes['giftBoxSkuSelectModal__unitGrid']}>
            {modalData.options.map((option) => {
              const selectedUnit = getSelectedUnit(option, selection);
              const selectedUnitValue = selectedUnit ? String(selectedUnit.id) : String(option.units[0]?.id ?? '');

              const currentQuantity = getQuantity(option.id, selection);
              const isSoldOut = selectedUnit?.stockStatus === 'sold-out';
              const isDiscontinued = selectedUnit?.stockStatus === 'discontinued';
              const shouldShowCounter = !isSoldOut && !isDiscontinued;
              const cannotIncrementByStock = selectedUnit ? currentQuantity >= selectedUnit.stock : true;
              const cannotIncrementByCapacity =
                modalData.giftBoxMaxPoint > 0 && selectedUnit
                  ? usedCapacity + selectedUnit.sizePoint > modalData.giftBoxMaxPoint
                  : true;

              return (
                <article
                  key={option.id}
                  className={classes['giftBoxSkuSelectModal__unitCard']}
                >
                  <div className={classes['giftBoxSkuSelectModal__unitSelectedContent']}>
                    {selectedUnit ? (
                      <div className={classes['giftBoxSkuSelectModal__unitPreviewWrapper']}>
                        {selectedUnit.imageUrl ? (
                          <img
                            className={classes['giftBoxSkuSelectModal__unitPreviewImage']}
                            src={selectedUnit.imageUrl}
                            alt={selectedUnit.name}
                          />
                        ) : (
                          <div className={classes['giftBoxSkuSelectModal__unitPreviewPlaceholder']}>SKU</div>
                        )}
                      </div>
                    ) : null}
                    <div className={classes['giftBoxSkuSelectModal__unitBody']}>
                      <h4 className={classes['giftBoxSkuSelectModal__unitName']}>{option.name}</h4>
                      <label className={classes['giftBoxSkuSelectModal__selectWrapper']}>
                        <select
                          className={classes['giftBoxSkuSelectModal__select']}
                          value={selectedUnitValue}
                          onChange={(event) => updateSelectedUnit(option.id, Number(event.target.value))}
                        >
                          {option.units.map((unit) => (
                            <option
                              key={unit.id}
                              value={unit.id}
                            >
                              {unit.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      {selectedUnit ? (
                        <div className={classes['giftBoxSkuSelectModal__unitSelectedBody']}>
                          <p className={classes['giftBoxSkuSelectModal__meta']}>{formatUnitMeta(selectedUnit)}</p>
                          {isSoldOut ? <p className={classes['giftBoxSkuSelectModal__stockLabel']}>売り切れ</p> : null}
                          {isDiscontinued ? (
                            <p className={classes['giftBoxSkuSelectModal__stockLabel']}>取扱終了</p>
                          ) : null}

                          {shouldShowCounter ? (
                            <div className={classes['giftBoxSkuSelectModal__counter']}>
                              <button
                                type="button"
                                className={classes['giftBoxSkuSelectModal__counterButton']}
                                onClick={() => updateQuantity(option.id, -1)}
                                disabled={currentQuantity <= 0}
                              >
                                -
                              </button>
                              <span className={classes['giftBoxSkuSelectModal__counterValue']}>{currentQuantity}</span>
                              <button
                                type="button"
                                className={classes['giftBoxSkuSelectModal__counterButton']}
                                onClick={() => updateQuantity(option.id, 1)}
                                disabled={cannotIncrementByStock || cannotIncrementByCapacity}
                              >
                                +
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section
          className={`${classes['giftBoxSkuSelectModal__section']} ${classes['giftBoxSkuSelectModal__section--summary']}`}
        >
          <h3 className={classes['giftBoxSkuSelectModal__sectionTitle']}>セレクト状況</h3>

          {summaryRows.length === 0 ? (
            <div className={classes['giftBoxSkuSelectModal__emptySummaryRow']}>
              <span>未選択</span>
              <span>-</span>
            </div>
          ) : (
            Object.values(groupedSummaryRows).map((rows) => (
              <section
                key={rows[0].optionId}
                className={classes['giftBoxSkuSelectModal__summaryGroup']}
              >
                <h4 className={classes['giftBoxSkuSelectModal__summaryGroupTitle']}>{rows[0].optionName}</h4>
                <div className={classes['giftBoxSkuSelectModal__summaryRows']}>
                  {rows.map((row) => (
                    <div
                      key={row.unitId}
                      className={classes['giftBoxSkuSelectModal__summaryRow']}
                    >
                      <span>
                        {row.unitName} x {row.quantity}
                      </span>
                      <span>{fmtPriceWithYen(row.price * row.quantity)}</span>
                    </div>
                  ))}
                </div>
              </section>
            ))
          )}

          <div className={classes['giftBoxSkuSelectModal__summaryPrices']}>
            <p className={classes['giftBoxSkuSelectModal__summaryPriceRow']}>
              <span>ボックス単体価格</span>
              <span>{fmtPriceWithYen(modalData.defaultUnitTotalPrice)}</span>
            </p>
            <p
              className={`${classes['giftBoxSkuSelectModal__summaryPriceRow']} ${classes['giftBoxSkuSelectModal__summaryPriceRow--total']}`}
            >
              <span>
                商品合計
                <span className={classes['giftBoxSkuSelectModal__summaryPriceTaxLabel']}>(税込)</span>
              </span>
              <span>{fmtPriceWithYen(totalPrice)}</span>
            </p>
          </div>
        </section>

        {cartMessage ? <p className={classes['giftBoxSkuSelectModal__cartMessage']}>{cartMessage}</p> : null}

        <div className={classes['giftBoxSkuSelectModal__actions']}>
          <Button
            label="構成をリセット"
            className={`${classes['giftBoxSkuSelectModal__actionButton']} ${classes['giftBoxSkuSelectModal__actionButton--reset']}`}
            onClick={resetSelection}
          />
          <Button
            label="カートに入れる"
            disabled={!hasSelectedItems || isAdding || isPreview}
            className={`${classes['giftBoxSkuSelectModal__actionButton']} ${classes['giftBoxSkuSelectModal__actionButton--submit']}`}
            onClick={handleCartButtonClick}
          />
          <Button
            label="閉じる"
            variant="secondary"
            className={`${classes['giftBoxSkuSelectModal__actionButton']} ${classes['giftBoxSkuSelectModal__actionButton--back']}`}
            onClick={closeModal}
          />
        </div>
      </>
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={classes['giftBoxSkuSelectModalOverlay']}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className={classes['giftBoxSkuSelectModal']}
        role="dialog"
        aria-modal="true"
        aria-label="ギフトボックスを作成する"
      >
        <div className={classes['giftBoxSkuSelectModal__body']}>
          <div className={classes['giftBoxSkuSelectModal__header']}>
            <p className={classes['giftBoxSkuSelectModal__title']}>ギフトボックスを作成する</p>
            <button
              type="button"
              className={classes['giftBoxSkuSelectModal__closeButton']}
              aria-label="閉じる"
              onClick={closeModal}
            >
              ×
            </button>
          </div>
          {renderBody()}
        </div>
      </div>
    </div>
  );
});

GiftBoxSkuSelectModal.displayName = 'GiftBoxSkuSelectModal';

export default GiftBoxSkuSelectModal;
